import type jsPDF from 'jspdf'

import { PDF_CONFIG, TWO_COLUMN_DIVIDER_TO_ROW_MM } from './resumeExportPdfConfig'
import { WORK_EXPERIENCE_TEAL_HEADER_BULLET } from './workExperienceRoleHeaderPreview'

export type CenteredContactRun = { text: string; w: number }

/** Layout contact segments into centered lines (last two segments stay together when possible). */
export function buildCenteredContactLineRuns(
  parts: string[],
  maxWidthMm: number,
  measure: (text: string) => number
): CenteredContactRun[][] {
  if (!parts.length) return []

  const bullet = WORK_EXPERIENCE_TEAL_HEADER_BULLET
  const outLines: CenteredContactRun[][] = []
  let line: CenteredContactRun[] = []
  let lineW = 0

  const flush = (): void => {
    if (line.length > 0) {
      outLines.push(line)
      line = []
      lineW = 0
    }
  }

  const push = (text: string): void => {
    const w = measure(text)
    if (line.length > 0 && lineW + w > maxWidthMm) flush()
    line.push({ text, w })
    lineW += w
  }

  const head = parts.length > 2 ? parts.slice(0, -2) : []
  const tail = parts.length >= 2 ? parts.slice(-2) : parts

  for (let i = 0; i < head.length; i++) {
    if (i > 0) push(bullet)
    push(head[i]!)
  }

  if (parts.length === 1) {
    push(parts[0]!)
  } else if (parts.length === 2) {
    push(tail[0]!)
    push(bullet)
    push(tail[1]!)
  } else {
    const tailChunk = `${tail[0]}${bullet}${tail[1]}`
    const tailW = measure(tailChunk)
    const bulletW = measure(bullet)
    if (line.length > 0) {
      const need = lineW + bulletW + tailW
      if (need > maxWidthMm) {
        flush()
        push(tailChunk)
      } else {
        push(bullet)
        push(tailChunk)
      }
    } else {
      push(tailChunk)
    }
  }
  flush()

  return outLines
}

export type ResumePdfHeaderContext = {
  pdf: jsPDF
  leftX: number
  fullContentW: number
  getY: () => number
  setY: (y: number) => void
  checkPageBreak: (requiredHeightMm: number) => void
  ptToMm: (pt: number) => number
  normalizeText?: (text: string) => string
  /** Called before each draw (e.g. activate correct jsPDF page). */
  beforeDraw?: () => void
}

export type DrawResumePdfHeaderOptions = {
  fullName: string
  contactParts: string[]
  nameColor: [number, number, number]
  /** Gap after divider line — matches two-column preview `mt-4` on column row. */
  afterDividerGapMm?: number
}

function defaultNormalize(text: string): string {
  const sep = WORK_EXPERIENCE_TEAL_HEADER_BULLET
  const placeholder = '\uE000'
  return String(text || '')
    .replace(/\u00ad/g, '')
    .replace(/\u200b/g, '')
    .split(sep)
    .join(placeholder)
    .replace(/[\u2022\u25CF\uF0B7]/g, ' ')
    .split(placeholder)
    .join(sep)
    .replace(/\s+/g, ' ')
    .trim()
}

/** Name + wrapped contact row + divider — shared by classic and two-column PDF export. */
export function drawResumePdfHeader(
  ctx: ResumePdfHeaderContext,
  options: DrawResumePdfHeaderOptions
): void {
  const normalize = ctx.normalizeText ?? defaultNormalize
  const afterGap = options.afterDividerGapMm ?? TWO_COLUMN_DIVIDER_TO_ROW_MM

  const drawCenteredWrapped = (
    text: string,
    sizePt: number,
    bold: boolean,
    lineHeightMul: number,
    color: [number, number, number]
  ): void => {
    const t = normalize(text)
    if (!t) return
    ctx.pdf.setFont('helvetica', bold ? 'bold' : 'normal')
    ctx.pdf.setFontSize(sizePt)
    ctx.pdf.setTextColor(color[0], color[1], color[2])
    const lineH = ctx.ptToMm(sizePt) * lineHeightMul
    const lines = ctx.pdf.splitTextToSize(t, ctx.fullContentW)
    for (const line of lines) {
      ctx.checkPageBreak(lineH)
      ctx.beforeDraw?.()
      const w = ctx.pdf.getTextWidth(line)
      const x = ctx.leftX + Math.max(0, (ctx.fullContentW - w) / 2)
      const y = ctx.getY()
      ctx.pdf.text(line, x, y)
      ctx.setY(y + lineH)
    }
  }

  drawCenteredWrapped(
    options.fullName,
    PDF_CONFIG.fontSize.name,
    true,
    PDF_CONFIG.lineHeight.name,
    options.nameColor
  )

  if (options.contactParts.length > 0) {
    const sizePt = PDF_CONFIG.fontSize.contact
    const lineH = ctx.ptToMm(sizePt) * PDF_CONFIG.lineHeight.body

    const measure = (text: string): number => {
      ctx.pdf.setFont('helvetica', 'normal')
      ctx.pdf.setFontSize(sizePt)
      return ctx.pdf.getTextWidth(text)
    }

    const lines = buildCenteredContactLineRuns(options.contactParts, ctx.fullContentW, measure)

    for (const runs of lines) {
      const totalW = runs.reduce((sum, r) => sum + r.w, 0)
      ctx.checkPageBreak(lineH)
      ctx.beforeDraw?.()
      let drawX = ctx.leftX + Math.max(0, (ctx.fullContentW - totalW) / 2)
      const y = ctx.getY()
      ctx.pdf.setFont('helvetica', 'normal')
      ctx.pdf.setFontSize(sizePt)
      ctx.pdf.setTextColor(0, 0, 0)
      for (const run of runs) {
        ctx.pdf.text(run.text, drawX, y)
        drawX += run.w
      }
      ctx.setY(y + lineH)
    }

    ctx.beforeDraw?.()
    const y = ctx.getY()
    ctx.pdf.setDrawColor(0, 0, 0)
    ctx.pdf.setLineWidth(0.3)
    ctx.pdf.line(ctx.leftX, y, ctx.leftX + ctx.fullContentW, y)
    ctx.setY(y + afterGap)
  } else {
    ctx.setY(ctx.getY() + afterGap)
  }
}
